import {createMergeableStore, type MergeableStore} from "tinybase";
import {createLocalPersister} from "tinybase/persisters/persister-browser";
import {createIndexedDbPersister} from "tinybase/persisters/persister-indexed-db";
import {createBroadcastChannelSynchronizer} from "tinybase/synchronizers/synchronizer-broadcast-channel";

// Where a repo's store lives on this device: an IndexedDB database per repo, or localStorage
// where IndexedDB doesn't work. No React in here, so the service worker can use it too.
//
// tinybase persisters *replace* the store's content when they load. So nothing here loads into a
// live store: reads go into a scratch store and get merged, which can't drop an edit that only
// exists in memory or that another tab saved in the meantime. (Except: tinybase drops a whole
// payload with a timestamp more than 5 minutes in the future, see the upgrade plan's risk 1.)

export const storageName = (key: string) => `db:${key}`;

const probeIndexedDb = async () =>
  new Promise<boolean>((res) => {
    if (typeof indexedDB === "undefined") {
      res(false);
      return;
    }
    try {
      const req = indexedDB.open("liste4000:probe");
      req.onsuccess = () => {
        req.result.close();
        res(true);
      };
      req.onerror = () => res(false);
      req.onblocked = () => res(false);
    } catch {
      res(false);
    }
  });

let indexedDbWorks: Promise<boolean> | undefined;
export const canUseIndexedDb = async () => (indexedDbWorks ??= probeIndexedDb());

const readInto = async (scratch: MergeableStore, key: string, useIndexedDb: boolean) => {
  // the localStorage copy is read even when IndexedDB is in use: it has everything from before the
  // switch, and whatever an app version from before the switch (a stale tab) keeps writing there.
  // It's never written to again, so once merged it can't win over anything newer. Service workers
  // have no localStorage.
  if (typeof localStorage !== "undefined") {
    await createLocalPersister(scratch, storageName(key)).load();
  }
  if (!useIndexedDb) return;
  const fromIndexedDb = createMergeableStore();
  let readError: unknown = null;
  await createIndexedDbPersister(fromIndexedDb, storageName(key), undefined, (e) => {
    readError ??= e;
  }).load();
  // The persister swallows read errors and leaves the store empty. Saving that would overwrite
  // the stored data, so fail instead, unless the database has no object store yet: then nothing
  // was ever saved.
  if (readError && !(readError instanceof DOMException && readError.name === "NotFoundError")) {
    throw readError instanceof Error
      ? readError
      : new Error("reading IndexedDB failed", {cause: readError});
  }
  scratch.merge(fromIndexedDb);
};

// Merges what this device has stored for `key` into `store`.
export const mergeStored = async (store: MergeableStore, key: string) => {
  const scratch = createMergeableStore();
  await readInto(scratch, key, await canUseIndexedDb());
  store.applyMergeableChanges(scratch.getMergeableContent());
};

export type RepoStorage = {
  // Merges what's stored first, and tells tabs that have the repo open to pick up the result.
  save: () => Promise<void>;
  // Saves on every change and exchanges changes with other tabs holding the same repo, until the
  // returned function is called.
  start: () => Promise<() => Promise<void>>;
};

export const createRepoStorage = async (
  store: MergeableStore,
  key: string
): Promise<RepoStorage> => {
  const useIndexedDb = await canUseIndexedDb();
  if (useIndexedDb) requestPersistentStorage();
  // never loaded (see above), only saved
  const persister = useIndexedDb
    ? createIndexedDbPersister(store, storageName(key))
    : createLocalPersister(store, storageName(key));
  // An open tab only hears about changes through the tab sync below, and would overwrite anything
  // else on its next auto-save: a save from a tab where the repo isn't in use, or from the service
  // worker. Those saves say so here, and open tabs merge and save again.
  const saved =
    typeof BroadcastChannel === "undefined"
      ? null
      : new BroadcastChannel(`liste4000:saved:${storageName(key)}`);
  const mergeAndSave = async () => {
    await mergeStored(store, key);
    await persister.save();
  };
  return {
    save: async () => {
      await mergeAndSave();
      saved?.postMessage(null);
    },
    start: async () => {
      const onSaved = () => {
        mergeAndSave().catch((e: unknown) => console.warn("merging a save failed", e));
      };
      saved?.addEventListener("message", onSaved);
      await persister.startAutoSave();
      // Without this, tabs only see each other's changes via the sync server, if at all. The
      // localStorage persister got that from `storage` events; the IndexedDB one would poll.
      const tabs =
        typeof BroadcastChannel === "undefined"
          ? null
          : createBroadcastChannelSynchronizer(store, `liste4000:${storageName(key)}`);
      // not awaited: with no other tab open, its first request only ends with a 1s timeout
      void tabs?.startSync();
      return async () => {
        saved?.removeEventListener("message", onSaved);
        await Promise.all([persister.stopAutoSave(), tabs?.destroy()]);
      };
    },
  };
};

// Asks the browser not to evict our storage. Only in the installed app: Firefox would show a
// permission prompt, and Chromium grants it to installed apps without asking.
let requestedPersistence = false;
const requestPersistentStorage = () => {
  if (requestedPersistence) return;
  requestedPersistence = true;
  // missing in old browsers
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof navigator === "undefined" || !navigator.storage) return;
  // no matchMedia in the service worker
  if (typeof matchMedia !== "function" || !matchMedia("(display-mode: standalone)").matches) return;
  void navigator.storage.persisted().then((isPersisted) => {
    if (!isPersisted) void navigator.storage.persist();
  });
};
